import { PiggyBank, Landmark, Users, FileText, Layers, TrendingUp, ExternalLink, Info } from 'lucide-react';

// Curated, source-backed catalog of investment categories relevant to El Salvador.
// Intentionally NO rates/yields — those change constantly; we link to official
// sources instead so the info stays honest.
const OPTIONS = [
  {
    title: 'Cuenta de ahorro',
    icon: PiggyBank,
    desc: 'Ahorro a la vista en un banco regulado. Bajo riesgo y disponible cuando lo necesitas; los rendimientos suelen ser modestos.',
    risk: 'Bajo', liquidity: 'Alta',
    sources: [{ label: 'Tasas (SSF)', url: 'https://ssf.gob.sv/tasas-de-interes-comisiones-y-recargos/' }],
  },
  {
    title: 'Depósito a plazo (DPF)',
    icon: Landmark,
    desc: 'Depositas por un plazo fijo a cambio de una tasa pactada. Bajo riesgo, pero el dinero queda comprometido hasta el vencimiento.',
    risk: 'Bajo', liquidity: 'Baja',
    sources: [
      { label: 'Tasas (SSF)', url: 'https://ssf.gob.sv/tasas-de-interes-comisiones-y-recargos/' },
      { label: 'Estadísticas (BCR)', url: 'https://estadisticas.bcr.gob.sv/' },
    ],
  },
  {
    title: 'Cooperativas de ahorro y crédito',
    icon: Users,
    desc: 'Ahorro y crédito en cooperativas. Confirma que esté supervisada: la regulación pasa a BCR y SSF (INSAFOCOOP en transición).',
    risk: 'Bajo–Medio', liquidity: 'Media',
    sources: [
      { label: 'Supervisadas (SSF)', url: 'https://ssf.gob.sv/supervisadas/' },
      { label: 'INSAFOCOOP', url: 'https://insafocoop.gob.sv/' },
    ],
  },
  {
    title: 'Bonos y deuda (renta fija)',
    icon: FileText,
    desc: 'Títulos de deuda del Estado o de empresas (p. ej. LETES y bonos) que se negocian en la Bolsa. Riesgo medio según el emisor.',
    risk: 'Medio', liquidity: 'Media',
    sources: [{ label: 'Bolsa de Valores', url: 'https://www.bolsadevalores.com.sv/' }],
  },
  {
    title: 'Fondos de inversión',
    icon: Layers,
    desc: 'Un gestor regulado invierte tu dinero en una cartera diversificada. El riesgo varía según el tipo de fondo.',
    risk: 'Variable', liquidity: 'Media',
    sources: [
      { label: 'Bolsa de Valores', url: 'https://www.bolsadevalores.com.sv/' },
      { label: 'Entidades (SSF)', url: 'https://ssf.gob.sv/supervisadas/' },
    ],
  },
  {
    title: 'Acciones (renta variable)',
    icon: TrendingUp,
    desc: 'Participación en el capital de empresas listadas. Mayor potencial de rendimiento y también mayor riesgo y volatilidad.',
    risk: 'Alto', liquidity: 'Media',
    sources: [{ label: 'Bolsa de Valores', url: 'https://www.bolsadevalores.com.sv/' }],
  },
];

const REGULATORS = [
  { label: 'Bolsa de Valores de El Salvador', url: 'https://www.bolsadevalores.com.sv/' },
  { label: 'Superintendencia del Sistema Financiero', url: 'https://ssf.gob.sv/' },
  { label: 'Banco Central de Reserva', url: 'https://www.bcr.gob.sv/' },
];

function riskColor(risk) {
  const r = risk.toLowerCase();
  if (r.includes('muy alto')) return 'var(--danger)';
  if (r.includes('alto')) return 'var(--danger)';
  if (r.includes('medio')) return 'var(--warning)';
  if (r.includes('variable')) return 'var(--info)';
  return 'var(--success)'; // bajo
}

function Badge({ label, value, color }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900/50 border border-white/10 px-2 py-1 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium" style={{ color: color || 'var(--text)' }}>{value}</span>
    </span>
  );
}

export default function Investments() {
  return (
    <div className="space-y-6" data-testid="investments-page">
      <div className="sticky top-[env(safe-area-inset-top)] z-10 bg-surface pb-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-white">Inversiones</h1>
        <p className="text-slate-400 text-sm mt-1">Opciones para hacer crecer tu dinero en El Salvador</p>
      </div>

      {/* Not-advice disclaimer */}
      <div className="rounded-2xl bg-amber-500/10 border border-amber-500/30 p-4 flex items-start gap-3" data-testid="investments-disclaimer">
        <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-200/90">
          Información educativa, <span className="font-medium">no asesoría financiera</span>. Las tasas, condiciones y riesgos
          cambian con el tiempo — verifica siempre con cada institución y su regulador antes de invertir.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          return (
            <div key={opt.title} className="glass-card rounded-2xl p-5 flex flex-col" data-testid={`investment-${opt.title}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-violet-500/15 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-violet-400" />
                </div>
                <h3 className="text-base font-semibold text-white">{opt.title}</h3>
              </div>
              <p className="text-sm text-slate-400 flex-1">{opt.desc}</p>
              <div className="flex flex-wrap items-center gap-2 mt-4">
                <Badge label="Riesgo" value={opt.risk} color={riskColor(opt.risk)} />
                <Badge label="Liquidez" value={opt.liquidity} />
              </div>
              <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/5">
                {opt.sources.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-violet-400 hover:text-violet-300 transition-colors"
                  >
                    {s.label} <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Official regulators / sources */}
      <div className="glass-card rounded-2xl p-5">
        <h2 className="text-sm font-semibold text-slate-300 mb-3">Fuentes oficiales</h2>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          {REGULATORS.map((r) => (
            <a
              key={r.url}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-violet-400 transition-colors"
            >
              {r.label} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
